import React from 'react';
import { MutationResult } from 'react-apollo';
// @ts-ignore
import Config from 'Config';
import Box from 'components/Box';
import isString from 'lodash/isString';
import Link from 'components/Link';
import assertions from 'lib/assertions';
// @ts-ignore
import EVENTS from 'integrations/events';
import Field from 'components/Field';
import Input from 'components/Input';
import Button from 'components/Button';
import BoxBody from 'components/BoxBody';
import EventTracking from 'lib/EventTracking';
import Columns2 from 'components/Columns2';
import HelpIcon from 'components/HelpIcon';
import ToastActions from 'actions/ToastActions';
import BoxHeader from 'components/BoxHeader';
import BoxFooter from 'components/BoxFooter';
import FormElement from 'components/FormElement';
import Label, { asterisked } from 'components/Label';
import ClipboardTrigger from 'components/ClipboardTrigger';
// @ts-ignore
import LinkedAccountProviderOauthConfigContainer from '../containers/LinkedAccountProviderOauthConfigContainer';

import { LinkedAccountProviderOauthConfigContainer as LinkedAccountProviderOauthConfigContainerQuery } from '../queries/__generated__/LinkedAccountProviderOauthConfigContainer';
import { UpdateOauthConfig } from '../queries/__generated__/UpdateOauthConfig';
import './styles/LinkedAccountProviderOauthConfigForm.scss';

const INVALID = 'INVALID';

type State = {
  authUrl?: string;
  clientId?: string;
  tokenUrl?: string;
  clientSecret?: string;
  scopes?: string | string[];
};

type Props = {
  uuid: string;
};

class LinkedAccountProviderOauthConfigForm extends React.Component<Props, State> {
  state: State = {};

  componentDidMount() {
    EventTracking.trackKey(EVENTS.OAUTH_FORM.VIEW, {
      organization_uuid: this.props.uuid,
    });
  }

  isSubmittable = () => {
    return Object.values(this.state).every(e => e !== INVALID);
  };

  submit(update: Function) {
    EventTracking.trackKey(EVENTS.OAUTH_FORM.SUBMIT, {
      organization_uuid: this.props.uuid,
    });

    update(this.state)
      .then(() => ToastActions.showSuccessToast('Saved!'))
      .catch((e: any) => {
        console.debug(e);
        console.error('LinkedAccountProviderOauthConfigForm - could not submit');
        ToastActions.showErrorToast(e.toString());
      });
  }

  render() {
    return (
      <LinkedAccountProviderOauthConfigContainer uuid={this.props.uuid}>
        {({
          linkedAccountProviderOauthConfig,
          contentSourceProviderKey,
          update,
          loading,
        }: {
          linkedAccountProviderOauthConfig: LinkedAccountProviderOauthConfigContainerQuery['linkedAccountProviderOauthConfig'];
          contentSourceProviderKey: LinkedAccountProviderOauthConfigContainerQuery['contentSourceProvider'];
          update: (arg0: any) => Promise<MutationResult<UpdateOauthConfig>>;
          loading: boolean;
        }) => {
          if (loading) {
            return null;
          }

          if (!isString(contentSourceProviderKey) || contentSourceProviderKey.length === 0) {
            return <div>You cannot setup OAuth without configuring content setting.</div>;
          }

          const { R101_FRONTEND_URL } = Config.Environment;
          const callbackURI = `${R101_FRONTEND_URL}/v2/users/linked_accounts/${contentSourceProviderKey}/callback`;

          return (
            <Box>
              <BoxHeader title="OAuth Config" />
              <BoxBody>
                <div>
                  <Columns2>
                    <FormElement
                      element={Input}
                      showErrorBeforeBlur
                      onValidChange={e => this.setState({ clientId: e.target.value })}
                      onInvalidChange={() => this.setState({ clientId: INVALID })}
                      label={asterisked('Client id')}
                      validate={e => {
                        assertions.notEmpty(e.target.value);
                      }}
                      elementProps={{
                        disabled: loading,
                        placeholder: 'abc',
                        defaultValue:
                          linkedAccountProviderOauthConfig &&
                          linkedAccountProviderOauthConfig.clientId,
                      }}
                    />
                    <FormElement
                      element={Input}
                      showErrorBeforeBlur
                      onValidChange={e => this.setState({ clientSecret: e.target.value })}
                      onInvalidChange={() => this.setState({ clientSecret: INVALID })}
                      label={asterisked('Client secret')}
                      validate={e => {
                        assertions.notEmpty(e.target.value);
                      }}
                      elementProps={{
                        disabled: loading,
                        placeholder: 'xyz',
                        defaultValue:
                          linkedAccountProviderOauthConfig &&
                          linkedAccountProviderOauthConfig.clientSecret,
                      }}
                    />
                  </Columns2>
                  <FormElement
                    element={Input}
                    showErrorBeforeBlur
                    onValidChange={e => this.setState({ authUrl: e.target.value })}
                    onInvalidChange={() => this.setState({ authUrl: INVALID })}
                    label={asterisked('Auth URL')}
                    validate={e => {
                      assertions.notEmpty(e.target.value);
                      assertions.url(e.target.value);
                    }}
                    elementProps={{
                      disabled: loading,
                      placeholder: 'https://www.acmeinc.com/oauth',
                      defaultValue:
                        linkedAccountProviderOauthConfig &&
                        linkedAccountProviderOauthConfig.authUrl,
                    }}
                  />
                  <FormElement
                    element={Input}
                    showErrorBeforeBlur
                    onValidChange={e => this.setState({ tokenUrl: e.target.value })}
                    onInvalidChange={() => this.setState({ tokenUrl: INVALID })}
                    label={asterisked('Token URL')}
                    validate={e => {
                      assertions.notEmpty(e.target.value);
                      assertions.url(e.target.value);
                    }}
                    elementProps={{
                      disabled: loading,
                      placeholder: 'https://www.acmeinc.com/oauth/token',
                      defaultValue:
                        linkedAccountProviderOauthConfig &&
                        linkedAccountProviderOauthConfig.tokenUrl,
                    }}
                  />
                  <FormElement
                    element={Input}
                    showErrorBeforeBlur
                    onValidChange={e =>
                      this.setState({
                        scopes: e.target.value.split(',').map((s: string) => s.trim()),
                      })
                    }
                    onInvalidChange={() => this.setState({ scopes: INVALID })}
                    label="Scopes (comma separated)"
                    elementProps={{
                      disabled: loading,
                      placeholder: 'read, write, update',
                      defaultValue:
                        linkedAccountProviderOauthConfig &&
                        linkedAccountProviderOauthConfig.scopes.join(', '),
                    }}
                  />

                  <Field>
                    <Label>
                      <span>
                        Redirect URI
                        {' - '}
                        <ClipboardTrigger
                          text={callbackURI}
                          onCopy={() => ToastActions.showSuccessToast('Copied')}>
                          <Link>copy</Link>
                        </ClipboardTrigger>
                        {' - '}
                        <HelpIcon
                          tooltipText={
                            'This redirect URI will also be passed as a redirect_uri query parameter to your auth URL'
                          }
                        />
                      </span>
                    </Label>
                    <Input disabled value={callbackURI} />
                  </Field>
                </div>
              </BoxBody>
              <BoxFooter>
                <Button
                  onClick={() => this.submit(update)}
                  disabled={loading || !this.isSubmittable()}>
                  Save
                </Button>
              </BoxFooter>
            </Box>
          );
        }}
      </LinkedAccountProviderOauthConfigContainer>
    );
  }
}

export default LinkedAccountProviderOauthConfigForm;
